import React from 'react';
import { Check, Plus, RefreshCw, Settings2, User, Users } from 'lucide-react';
import { useTheme } from '../../../contexts/ThemeContext';
import { cn } from '../../../lib/utils';
import { useDashboardFilters, formatPeriodForTimeRange, type DashboardScope } from '../contexts/DashboardFiltersContext';
import TimeRangeSelector, { CalendarIcon } from './TimeRangeSelector';
import DashboardSwitcher from './DashboardSwitcher';
import {
  actionBtnClass,
  filterBarClass,
  filterDividerClass,
  filterGroupClass,
  filterLabelClass,
  segmentedControlClass,
  segmentedOptionClass,
  toolbarBtnClass,
} from '../themeHelpers';

export interface DashboardFilterBarProps {
  /** When set, show dashboard switcher (current name and save-as) */
  currentLayoutName?: string;
  onSaveAsDashboard?: () => void;
  /** Open dashboard settings (widgets, rename/delete dashboards) */
  onManageDashboards?: () => void;
  /** Reload dashboard layout from server */
  onRefresh?: () => void;
  /** Customize mode active (edit layout) */
  isCustomizing?: boolean;
  /** Toggle customize / edit mode */
  onCustomizeToggle?: () => void;
  /** Open dashboard settings (add widgets, groups) */
  onOpenSettings?: () => void;
}

const DashboardFilterBar: React.FC<DashboardFilterBarProps> = ({
  currentLayoutName,
  onSaveAsDashboard,
  onManageDashboards,
  onRefresh,
  isCustomizing = false,
  onCustomizeToggle,
  onOpenSettings,
}) => {
  const { colors } = useTheme();
  const { timeRange, setTimeRange, scope, setScope } = useDashboardFilters();

  return (
    <header className={filterBarClass(colors)}>
      {onSaveAsDashboard != null && (
        <div className="flex shrink-0 items-center sm:mr-6">
          <DashboardSwitcher
            currentLayoutName={currentLayoutName}
            onSaveAsClick={onSaveAsDashboard}
            onManageClick={onManageDashboards}
          />
        </div>
      )}

      <div className={filterGroupClass(colors)}>
        <div className="flex min-w-0 flex-1 flex-col gap-2 sm:flex-row sm:items-center sm:gap-3">
          <div className={filterLabelClass(colors)}>
            <CalendarIcon className="h-3.5 w-3.5" />
            <span>Time</span>
          </div>
          <div className="flex min-w-0 flex-wrap items-center gap-x-3 gap-y-1">
            <div className="-mx-1 min-w-0 touch-pan-x overflow-x-auto px-1 sm:mx-0 sm:px-0">
              <TimeRangeSelector value={timeRange} onChange={setTimeRange} />
            </div>
            <span className={cn('text-sm tabular-nums', colors.textMuted)}>
              {timeRange === 'all' ? '(No Time Range)' : `(Date Period: ${formatPeriodForTimeRange(timeRange)})`}
            </span>
          </div>
        </div>
        <div className={filterDividerClass(colors)} aria-hidden />
        <div className="flex min-w-0 flex-col gap-2 sm:flex-row sm:items-center sm:gap-3">
          <span className={cn('shrink-0 text-xs font-semibold uppercase tracking-wider', colors.textMuted)}>
            Scope
          </span>
          <div className={segmentedControlClass(colors)}>
            {(['myWork', 'team'] as DashboardScope[]).map((option) => {
              const isSelected = scope === option;
              const isMyWork = option === 'myWork';
              return (
                <button
                  key={option}
                  type="button"
                  onClick={() => setScope(option)}
                  className={cn(segmentedOptionClass(colors, isSelected, 'solid'), 'gap-2')}
                >
                  {isMyWork ? (
                    <User className="h-4 w-4 shrink-0" aria-hidden />
                  ) : (
                    <Users className="h-4 w-4 shrink-0" aria-hidden />
                  )}
                  <span>{isMyWork ? 'My Work' : 'Team'}</span>
                </button>
              );
            })}
          </div>
        </div>
      </div>

      <div className="flex shrink-0 items-center gap-0.5 self-center sm:ml-4 sm:self-auto">
        {onCustomizeToggle != null && (
          <button
            type="button"
            onClick={onCustomizeToggle}
            className={toolbarBtnClass(colors, isCustomizing)}
            title={isCustomizing ? 'Done editing' : 'Customize dashboard'}
            aria-label={isCustomizing ? 'Done editing' : 'Customize dashboard'}
          >
            {isCustomizing ? (
              <Check className="h-4 w-4 shrink-0" aria-hidden />
            ) : (
              <Settings2 className="h-4 w-4 shrink-0" aria-hidden />
            )}
          </button>
        )}
        {onOpenSettings != null && (
          <button
            type="button"
            onClick={onOpenSettings}
            className={actionBtnClass(colors)}
            title="Add widgets"
            aria-label="Add widgets"
          >
            <Plus className="h-4 w-4 shrink-0" aria-hidden />
          </button>
        )}
        {onRefresh != null && (
          <button
            type="button"
            onClick={onRefresh}
            className={actionBtnClass(colors)}
            title="Refresh dashboard"
            aria-label="Refresh dashboard"
          >
            <RefreshCw className="h-4 w-4" aria-hidden />
          </button>
        )}
      </div>
    </header>
  );
};

export default DashboardFilterBar;
